import { z } from 'zod';

/**
 * SIL-2372 (Bench R2 P3-01): the passport view assembled from the evidence ledger (passport_evidence, 1212+),
 * for the buyer (QR fingerprint token) and the owner (asset customer_org, D15). Design:
 * docs/superpowers/specs/2026-10-29-bench-r2-passport-design.md section 11, D3, D8, D9, D11, D12, D16.
 *
 * Shaped for the web passport (P3-02) so the UI renders it directly, owner decision 2026-10-30 (variation B with the
 * component groups of variation E):
 *   header            unit identity + the three REC results (data status, functional F, cosmetic C)
 *   componentGroups   GPUs, Drives, Memory, CPUs and chassis (+ other parts when present); each card has its REC result,
 *                     masked id, and either a "Component passport" link target or a reason there is none
 *   results           latest result per component and function (raw measurements; no raw files)
 *   history           every entry, oldest first, older fails and corrections included (design 16 call 3)
 *   configChanges     config.change entries: removed / added parts, masked, never linked (D9)
 *   evidence          entry hashes + signature state per entry; unsigned stays visible
 *
 * No FMV and no owner name exist in this shape in either audience: FMV stays in the existing owner-only passport sections.
 * The buyer audience carries no hardware identifier (D12) and no raw evidence file; the owner audience adds identifiers
 * and evidence file metadata. Wording follows design 5 / D3: results are stated as results, never as certification.
 */

/** Footer text, shown on every audience. It states what the passport is not (design 5, D3). */
export const PASSPORT_LEDGER_FOOTER_TEXT =
  'This passport states results recorded in the SiliconX evidence ledger. It is not an R2 certification and makes no claim of approval by any third party.';

export const PASSPORT_LEDGER_SIGNING_NOTE =
  'Entry signatures are checked on the verify page against the published key set. This passport reports whether a signature is present, not whether it is valid.';

export const passportLedgerAudienceSchema = z.enum(['buyer', 'owner']);
export type PassportLedgerAudience = z.infer<typeof passportLedgerAudienceSchema>;

export const PASSPORT_COMPONENT_GROUP_KEYS = ['gpus', 'drives', 'memory', 'cpus_chassis', 'other'] as const;
export const passportComponentGroupKeySchema = z.enum(PASSPORT_COMPONENT_GROUP_KEYS);
export type PassportComponentGroupKey = z.infer<typeof passportComponentGroupKeySchema>;

export const PASSPORT_COMPONENT_GROUP_LABELS: Record<PassportComponentGroupKey, string> = {
  gpus: 'GPUs',
  drives: 'Drives',
  memory: 'Memory',
  cpus_chassis: 'CPUs and chassis',
  other: 'Other parts',
};

const recFunctionalSchema = z.enum(['F3', 'F4']);
const functionStatusSchema = z.enum(['pass', 'fail', 'missing']);

const measurementSchema = z.object({ name: z.string(), value: z.string(), unit: z.string() }).strict();
const limitSchema = z
  .object({ name: z.string(), min: z.string().nullable(), max: z.string().nullable(), unit: z.string() })
  .strict();

/** Why a component shows no functional category. */
export const PASSPORT_NOT_F_REASON_TEXT = {
  no_functions: 'No test plan functions are recorded for this kind of part',
  key_function_failed: 'A key function did not pass',
  key_function_missing: 'A key function has no complete result',
  hardware_function_missing: 'A hardware function has no complete result',
  data_not_sanitized: 'Data devices do not all have a passing erase record',
  no_components: 'No component results are recorded',
} as const;

const recResultSchema = z
  .object({
    category: recFunctionalSchema.nullable(),
    /** REC Table 4 name of the category, or null. */
    categoryLabel: z.string().nullable(),
    /** Set when category is null: a key of PASSPORT_NOT_F_REASON_TEXT. */
    reason: z.string().nullable(),
    reasonText: z.string().nullable(),
  })
  .strict();
export type PassportRecResult = z.infer<typeof recResultSchema>;

/**
 * Where a component card sends the reader. "Component passport" is the only link label (never "Child passport").
 *   linked    own passport; `fingerprint` is its QR token (opens the same buyer view)
 *   none      no own passport. reason `no_serial`: a part with no usable serial (e.g. a DIMM) stays a line in the unit's
 *             inventory (design 8.2). reason `results_in_unit`: results live in the unit passport (e.g. CPUs).
 */
export const passportComponentLinkSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('linked'), label: z.literal('Component passport'), fingerprint: z.string() }).strict(),
  z
    .object({
      state: z.literal('none'),
      reason: z.enum(['no_serial', 'results_in_unit']),
      label: z.enum(['No component passport', 'Results in unit passport']),
    })
    .strict(),
]);
export type PassportComponentLink = z.infer<typeof passportComponentLinkSchema>;

export const passportComponentCardSchema = z
  .object({
    /** Stable within this response only (group-kind-index); not an identifier of the part. */
    key: z.string(),
    kind: z.string(),
    label: z.string(),
    modelName: z.string().nullable(),
    /** `****` + the last 2..4 alphanumerics (D9/D12), null when the part has no serial-kind identity. Both audiences. */
    maskedId: z.string().nullable(),
    /** OWNER ONLY: the unmasked identifier. Always null for the buyer. */
    identifier: z.string().nullable(),
    /** OWNER ONLY: the component's asset id. Always null for the buyer. */
    assetId: z.string().nullable(),
    rec: recResultSchema,
    functions: z.array(z.object({ functionId: z.string(), key: z.boolean(), status: functionStatusSchema }).strict()),
    link: passportComponentLinkSchema,
  })
  .strict();
export type PassportComponentCard = z.infer<typeof passportComponentCardSchema>;

export const passportResultItemSchema = z
  .object({
    entryId: z.string(),
    seq: z.number().int(),
    at: z.string(),
    /** Card key, or null for a unit-level result. */
    componentKey: z.string().nullable(),
    functionId: z.string(),
    verdict: z.enum(['pass', 'fail', 'incomplete', 'not_run']),
    durationSeconds: z.number().int().nullable(),
    measurements: z.array(measurementSchema),
    limits: z.array(limitSchema),
    tool: z.string().nullable(),
    toolVersion: z.string().nullable(),
  })
  .strict();
export type PassportResultItem = z.infer<typeof passportResultItemSchema>;

export const passportHistoryItemSchema = z
  .object({
    entryId: z.string(),
    seq: z.number().int(),
    at: z.string(),
    chain: z.enum(['self', 'component']),
    type: z.string(),
    componentKey: z.string().nullable(),
    /** One factual line, written by the assembler (design 5 wording). */
    summary: z.string(),
    /** True when a later correction names this entry. It stays visible, to buyers too. */
    superseded: z.boolean(),
  })
  .strict();
export type PassportHistoryItem = z.infer<typeof passportHistoryItemSchema>;

export const passportHistoryPageSchema = z
  .object({ truncated: z.boolean(), nextBefore: z.string().nullable(), componentsTruncated: z.boolean() })
  .strict();
export type PassportHistoryPage = z.infer<typeof passportHistoryPageSchema>;

/** SIL-2409 C5: the `?before=` cursor is opaque base64url of a composite keyset position; bounded so a hostile value is cheap to refuse. */
export const PASSPORT_LEDGER_CURSOR_MAX_LENGTH = 200;
export const passportLedgerCursorSchema = z
  .object({
    /** created_at as the database printed it (UTC, microseconds): never round-tripped through a JS Date. */
    createdAt: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/),
    /**
     * Position of the asset in the response's deterministic visible order (0 = the root, then its children by id). An ORDINAL, never
     * the asset id: the cursor is only base64url, so an id inside it would put an internal asset id in the public response.
     */
    assetOrdinal: z.number().int().min(0).max(64),
    seq: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();
export type PassportLedgerCursor = z.infer<typeof passportLedgerCursorSchema>;

export const passportConfigChangeSchema = z
  .object({
    entryId: z.string(),
    change: z.enum(['added', 'removed', 'swapped']),
    kind: z.string(),
    /** Masked tail or null. A removed part is never linked from the unit (D9). */
    maskedId: z.string().nullable(),
    changedOn: z.string(),
  })
  .strict();
export type PassportConfigChange = z.infer<typeof passportConfigChangeSchema>;

export const passportEvidenceEntrySchema = z
  .object({
    entryId: z.string(),
    seq: z.number().int(),
    chain: z.enum(['self', 'component']),
    type: z.string(),
    at: z.string(),
    /** sha256 entry hash, lowercase hex (the value the signature covers; see the verify page). */
    entryHash: z.string(),
    signature: z.object({ state: z.enum(['signed', 'unsigned']), keyIds: z.array(z.string()) }).strict(),
  })
  .strict();

export const passportEvidenceFileSchema = z
  .object({ entryId: z.string(), kind: z.string(), sizeBytes: z.number().int(), sha256: z.string() })
  .strict();

export const passportLedgerViewSchema = z
  .object({
    audience: passportLedgerAudienceSchema,
    /** `unit` = a top-level asset; `component` = a part with its own passport (D8). */
    subject: z.enum(['unit', 'component']),
    generatedAt: z.string(),
    header: z
      .object({
        fingerprint: z.string().nullable(),
        kind: z.string().nullable(),
        manufacturerName: z.string().nullable(),
        modelName: z.string().nullable(),
        /** `****` + tail for both audiences, when the subject has a serial-kind identity. */
        maskedId: z.string().nullable(),
        /** OWNER ONLY. */
        serialNumber: z.string().nullable(),
        /** OWNER ONLY. */
        assetId: z.string().nullable(),
        recStatus: z
          .object({
            data: z.object({ status: z.string().nullable(), label: z.string() }).strict(),
            functional: recResultSchema.extend({
              cappedByRequiredClass: z.boolean(),
              missingClasses: z.array(z.string()),
            }),
            cosmetic: z
              .object({
                code: z.string().nullable(),
                /** Appendix B F/C pair: valid | invalid_pair | no_cosmetic | invalid_cosmetic | no_functional. */
                pair: z.string(),
              })
              .strict(),
          })
          .strict(),
      })
      .strict(),
    componentGroups: z.array(
      z
        .object({
          key: passportComponentGroupKeySchema,
          label: z.string(),
          components: z.array(passportComponentCardSchema),
        })
        .strict(),
    ),
    results: z.array(passportResultItemSchema),
    history: z.array(passportHistoryItemSchema),
    /**
     * SIL-2409 C5: paging state of `history`. `truncated` is true when older entries exist than the ones returned (the public cap, the
     * size guard, or the owner row cap); `nextBefore` is the opaque `?before=` cursor of the LAST RETURNED (oldest) entry, null when
     * nothing older exists. `componentsTruncated`: more same-org child components exist than the 64 shown.
     */
    historyPage: passportHistoryPageSchema,
    configChanges: z.array(passportConfigChangeSchema),
    links: z.object({ unit: z.object({ fingerprint: z.string() }).strict().nullable() }).strict(),
    evidence: z
      .object({
        /** no_entries: the ledger holds nothing for this asset yet (results are "not evaluated", never a pass). */
        ledger: z.enum(['no_entries', 'present']),
        signing: z
          .object({
            state: z.enum(['no_entries', 'unsigned', 'partially_signed', 'all_signed']),
            signedEntries: z.number().int(),
            totalEntries: z.number().int(),
            note: z.string(),
          })
          .strict(),
        entries: z.array(passportEvidenceEntrySchema),
        /** OWNER ONLY (null for the buyer): metadata of raw evidence files. The files are never in this response. */
        files: z.array(passportEvidenceFileSchema).nullable(),
      })
      .strict(),
    footer: z.string(),
  })
  .strict();
export type PassportLedgerView = z.infer<typeof passportLedgerViewSchema>;
